import { useContext, useEffect, useMemo, useRef, useState } from "react";
import PopUp from "./PopUp";
import { AppContext } from "../App";
import api from "./api/api";
import Table from "./ui/Table";
import SearchIcon from "../icons/SearchIcon";
import FilterIcon from "../icons/FilterIcon";
import DateQuickPicker from "./ui/DateQuickPicker";

// Ordine kiosk/POS con PAX in errore: non si sa se la carta e' stata addebitata, decide la cassa (YELLGO-6292)
const isPaxUncertain = (order) => order.status == "payment_error" && order.payment_error_code == "PAX_ERROR";
// Scontrino gia' stampato dal PAX nonostante l'errore: confermato senza un nostro numero di scontrino
const PAX_RECEIPT = "PAX";

function SellsList() {
    const {
        config,
        fiscalPrinters,
        setSellsListVisible,
        sells,
        setConfirm,
        loadOrders,
        loadSells,
        sellsPagination,
        sellsSearch,
        setSellsSearch,
        apiSettings,
        updateOrder,
        sellsFilter,
        setSellsFilter,
        checkPermission,
        askPermission,
    } = useContext(AppContext);

    const [typeFilter, setTypeFilter] = useState("all");
    const [originFilter, setOriginFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [paidFilter, setPaidFilter] = useState("all");
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const firstSearchLoadRef = useRef(true);

    useEffect(() => {
        loadSells({ pag: sellsPagination?.pag || 1 });
    }, []);

    useEffect(() => {
        if (firstSearchLoadRef.current) {
            firstSearchLoadRef.current = false;
            return;
        }
        const timeoutId = window.setTimeout(() => {
            loadSells({ pag: 1, search: sellsSearch });
        }, 350);
        return () => window.clearTimeout(timeoutId);
    }, [sellsSearch]);

    const hasCustomerColumn = useMemo(() => {
        return (sells || []).some((order) =>
            Boolean(
                (order?.customer_id && Number(order.customer_id) > 0) ||
                String(order?.name || "").trim() ||
                String(order?.surname || "").trim() ||
                String(order?.email || "").trim() ||
                String(order?.mobile || "").trim(),
            ),
        );
    }, [sells]);

    const getOriginLabel = (origin) => {
        const map = {
            pos: "POS",
            website: "Website",
            selfordering: "Self Ordering",
            kiosk: "Kiosk",
            waiter: "Waiter",
            menu: "Menu",
            backend: "Backend",
            glovo: "Glovo",
            deliveroo: "Deliveroo",
            justeat: "Just Eat",
            ubereats: "Uber Eats",
            tracking: "Tracking",
            kitchenmonitor: "Kitchen Monitor",
            storedisplay: "Store Display",
        };
        if (map[origin]) return map[origin];
        return origin
            .split("_")
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ");
    };

    const getStatusLabel = (status) => {
        if (status == "unconfirmed") return "Non confermato";
        if (status == "confirmed") return "Confermato";
        if (status == "payment_error") return "Errore pagamento";
        if (status == "awaiting_payment") return "In attesa di pagamento";
        if (status == "awaiting_cash_payment") return "In attesa pagamento contanti";
        if (status == "archived") return "Annullato";
        if (status == "cancel_request") return "Richiesta annullo";
        if (status == "preparation") return "In preparazione";
        if (status == "filled") return "Evaso";
        if (status == "ready") return "Pronto";
        if (status == "delivering") return "In consegna";
        return status;
    };

    const getPaidState = (order) => {
        if (order.status == "payment_error" || order.status == "awaiting_payment") return "error";
        if (order.payment_method?.prepaid == 1) return "yes";
        return "no";
    };

    const typeOptions = useMemo(() => {
        const options = [
            { value: "shop", label: "Sala" },
            { value: "takeaway", label: "Asporto" },
            { value: "delivery", label: "Delivery" },
        ];
        const hasType = new Set(options.map((option) => option.value));
        for (const order of sells || []) {
            const value = String(order?.delivery_type || "").trim();
            if (value && !hasType.has(value)) {
                options.push({
                    value,
                    label: value.charAt(0).toUpperCase() + value.slice(1),
                });
                hasType.add(value);
            }
        }
        return options;
    }, [sells]);

    const originOptions = useMemo(() => {
        const knownOrigins = [
            "pos",
            "backend",
            "website",
            "selfordering",
            "menu",
            "kiosk",
            "waiter",
            "tracking",
            "kitchenmonitor",
            "storedisplay",
            "glovo",
            "deliveroo",
            "justeat",
            "ubereats",
        ];
        const mandantOriginMap = {
            website: "website",
            selfordering: "selfordering",
            dmenu: "menu",
            kiosk: "kiosk",
            waiter_app: "waiter",
            tracking: "tracking",
            kitchenmonitor: "kitchenmonitor",
            store_display: "storedisplay",
            glovo: "glovo",
            deliveroo: "deliveroo",
            justeat: "justeat",
            ubereats: "ubereats",
        };

        const set = new Set();
        const add = (value) => {
            const parsed = String(value || "").trim();
            if (parsed) set.add(parsed);
        };

        knownOrigins.forEach(add);
        Object.entries(mandantOriginMap).forEach(([flag, origin]) => {
            if (String(apiSettings?.mandant?.[flag] ?? "0") === "1") add(origin);
        });
        (apiSettings?.mandant?.clients || []).forEach((client) => add(client?.origin));
        Object.entries(apiSettings?.plugins || {}).forEach(([origin, plugin]) => {
            add(origin);
            if (String(plugin?.active ?? plugin?.enabled ?? "0") === "1") add(origin);
        });
        (sells || []).forEach((order) => add(order?.origin));

        return Array.from(set);
    }, [apiSettings, sells]);

    const statusOptions = useMemo(() => {
        const orderedStatuses = [
            "unconfirmed",
            "confirmed",
            "preparation",
            "ready",
            "delivering",
            "filled",
            "cancel_request",
            "awaiting_payment",
            "awaiting_cash_payment",
            "payment_error",
            "archived",
        ];
        const set = new Set(orderedStatuses);
        (sells || []).forEach((order) => {
            const status = String(order?.status || "").trim();
            if (status) set.add(status);
        });
        return Array.from(set);
    }, [sells]);

    const filteredSells = useMemo(() => {
        return (sells || []).filter((order) => {
            if (typeFilter !== "all" && String(order?.delivery_type || "") !== typeFilter) return false;
            if (originFilter !== "all" && String(order?.origin || "") !== originFilter) return false;
            if (statusFilter !== "all" && String(order?.status || "") !== statusFilter) return false;
            if (paidFilter !== "all" && getPaidState(order) !== paidFilter) return false;
            return true;
        });
    }, [sells, typeFilter, originFilter, statusFilter, paidFilter]);

    const hasSearch = sellsSearch.trim().length > 0;
    const hasActiveFilters =
        typeFilter !== "all" || originFilter !== "all" || statusFilter !== "all" || paidFilter !== "all";
    const pagination = sellsPagination || { pag: 1, per_pag: 25, total_records: 0, total_pages: 1 };
    const currentPage = Math.max(parseInt(pagination.pag || 1), 1);
    const totalPages = Math.max(parseInt(pagination.total_pages || 1), 1);
    const totalRecords = parseInt(pagination.total_records || 0);

    const goToPage = (pag) => {
        const nextPage = Math.max(1, Math.min(totalPages, pag));
        loadSells({ pag: nextPage, search: sellsSearch });
    };

    const refreshLists = async () => {
        await Promise.allSettled([loadOrders(), loadSells({ pag: currentPage, search: sellsSearch })]);
    };

    // L'elenco vendite non porta i pagamenti: se mancano si legge l'ordine completo
    const hasDojoPayment = async (order) => {
        let payments = order.payments;
        if (!Array.isArray(payments)) {
            const res = await api.postProtected("/orders/get/" + order.id + "/", {});
            payments = res.success == 1 ? res.data?.payments || [] : [];
        }
        return !!payments.find((p) => p.payment_method_id == 23);
    };

    // Rimborso del pagamento Dojo prima dell'annullo scontrino: se fallisce lo scontrino non viene annullato
    const refundDojoPayment = async (order) => {
        const res = await api.postProtected("/dojo/refund/", {
            order_id: order.id,
            reason: "Annulla vendita",
        });
        if (res.success == 1 || res.error_code == "DOJO_NOTHING_TO_REFUND") return true;
        setConfirm({
            title: "Rimborso non riuscito",
            message:
                res.error_code == "DOJO_REFUND_FORBIDDEN"
                    ? "Il tuo ruolo non è autorizzato al rimborso dei pagamenti elettronici (permesso POS 'Rimborso pagamenti')"
                    : res.error,
        });
        return false;
    };

    // archiveOrder: con RT l'archiviazione la fa l'agente solo se l'annullo fiscale riesce
    const cancelOrder = async (order, refundDojo = false, archiveOrder = true) => {
        console.log("cancelOrder", config.fiscal_printer_id, config.virtual_printer_id);
        if (refundDojo && !(await refundDojoPayment(order))) return;
        let changed = false;
        if (order.fiscal_printer_id) {
            const res = await api.postProtected("/fiscal_printers/send_command/", {
                command: "CANCEL_ORDER",
                order: order,
                archive_order: archiveOrder ? 1 : 0,
            });
            if (res.success == 0)
                setConfirm({
                    title: "Errore",
                    message: res.error,
                });
            else changed = true;
        }
        if (order.virtual_printer_id) {
            const res = await api.postProtected("/orders/edit/" + order.id + "/", {
                bill: 0,
                ...(archiveOrder ? { status: "archived" } : {}),
                command: "cancel_bill",
            });
            if (res.success == 0)
                setConfirm({
                    title: "Errore",
                    message: res.error,
                });
            else changed = true;
        }
        if (!order.fiscal_printer_id && !order.virtual_printer_id) {
            const res = await api.postProtected("/orders/edit/" + order.id + "/", {
                status: "archived",
            });
            if (res.success == 0)
                setConfirm({
                    title: "Errore",
                    message: res.error,
                });
            else changed = true;
        }
        if (changed) await refreshLists();
    };

    // Stampante fiscale per lo scontrino di un ordine gia' pagato: quella della cassa, oppure la prima del negozio,
    // ma mai un PAX (lo scontrino con pagamento elettronico farebbe partire un nuovo addebito, YELLGO-6292).
    const receiptPrinterId = () => {
        const printers = (fiscalPrinters || []).filter((p) => p.pax != 1);
        const own = printers.find((p) => p.id == config.fiscal_printer_id);
        return (own || printers[0])?.id || null;
    };

    const editOrder = async (order, request) => {
        console.log("editOrder");
        const res = await api.postProtected("/orders/edit/" + order.id + "/", request);
        if (res.success == 0)
            setConfirm({
                title: "Errore",
                message: res.error,
            });
        else await refreshLists();
    };

    return (
        <PopUp
            size="large"
            options={{
                onClose: () => setSellsListVisible(false),
                title: (
                    <div className="orders-list-title-row">
                        <span className="orders-list-title-with-badge">
                            <span className="orders-list-title-badge">{totalRecords}</span>
                            Vendite
                        </span>
                        <DateQuickPicker value={sellsFilter} onChange={setSellsFilter} />
                    </div>
                ),
                headerButtons: (
                    <div className="orders-list-header-controls">
                        <div className="orders-list-search-wrap">
                            <input
                                type="search"
                                value={sellsSearch}
                                onChange={(e) => setSellsSearch(e.target.value)}
                                placeholder="Cerca cliente o ID ordine"
                            />
                            <span className="orders-list-search-icon" aria-hidden="true">
                                <SearchIcon />
                            </span>
                        </div>
                        <button
                            type="button"
                            className={"orders-list-filters-toggle" + (showAdvancedFilters ? " active" : "")}
                            onClick={() => setShowAdvancedFilters((prev) => !prev)}
                            aria-label="Mostra o nascondi filtri"
                            aria-pressed={showAdvancedFilters}
                        >
                            <FilterIcon />
                        </button>
                    </div>
                ),
                content: (
                    <div className="orders-list-content">
                        {showAdvancedFilters && (
                            <div className="orders-list-table-filters">
                                <select
                                    className="orders-list-filter-select"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    <option value="all">Tipo</option>
                                    {typeOptions.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {"Tipo: " + type.label}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className="orders-list-filter-select"
                                    value={originFilter}
                                    onChange={(e) => setOriginFilter(e.target.value)}
                                >
                                    <option value="all">Origine</option>
                                    {originOptions.map((origin) => (
                                        <option key={origin} value={origin}>
                                            {"Origine: " + getOriginLabel(origin)}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className="orders-list-filter-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">Stato</option>
                                    {statusOptions.map((status) => (
                                        <option key={status} value={status}>
                                            {"Stato: " + getStatusLabel(status)}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className="orders-list-filter-select"
                                    value={paidFilter}
                                    onChange={(e) => setPaidFilter(e.target.value)}
                                >
                                    <option value="all">Pagato</option>
                                    <option value="yes">Pagato: si</option>
                                    <option value="no">Pagato: no</option>
                                    <option value="error">Pagato: errore</option>
                                </select>
                            </div>
                        )}
                        {(hasSearch || hasActiveFilters) && (
                            <div className="orders-list-search-results-label">risultati di ricerca</div>
                        )}
                        <div className="products-admin-table orders-admin-table">
                            <Table
                                header={true}
                                config={config}
                                type="sells"
                                cols={[
                                    { id: "id", label: "ID", type: "id", align: "center", width: "70px" },
                                    {
                                        id: "delivery_type",
                                        label: "Tipo",
                                        type: "delivery_type",
                                        align: "center",
                                        width: "72px",
                                    },
                                    {
                                        id: "delivery_date",
                                        label: "Ora",
                                        type: "delivery_date",
                                        align: "center",
                                        width: "78px",
                                    },
                                    {
                                        id: "origin",
                                        label: "Origine",
                                        type: "origin",
                                        align: "center",
                                        width: "92px",
                                    },
                                    {
                                        id: "customer",
                                        label: "Cliente",
                                        type: "customer",
                                        width: "108px",
                                        align: "left",
                                        visible: hasCustomerColumn,
                                    },
                                    {
                                        id: "info",
                                        label: "Info",
                                        type: "info",
                                        align: "left",
                                        width: "minmax(180px, 1.45fr)",
                                    },
                                    {
                                        id: "thermal_print_results",
                                        label: "Comande",
                                        type: "thermal_print_results",
                                        align: "left",
                                        width: "minmax(190px, 1.15fr)",
                                    },
                                    {
                                        id: "total",
                                        label: "Totale",
                                        type: "eur",
                                        align: "right",
                                        width: "84px",
                                    },
                                    {
                                        id: "bill",
                                        label: "Scontrino",
                                        type: "bill",
                                        align: "center",
                                        width: "120px",
                                        visible:
                                            apiSettings.mandant.fiscal_printers == 1 ||
                                            apiSettings.mandant.fiskaly == 1,
                                    },
                                    {
                                        id: "buttons",
                                        label: "",
                                        type: "buttons",
                                        align: "left",
                                        width: "322px",
                                        buttons: [
                                            {
                                                label: "Ristampa Comanda",
                                                onClick: (order) => {
                                                    setConfirm({
                                                        title: "Sei sicuro?",
                                                        onConfirm: () => {
                                                            editOrder(order, { printed: 0 });
                                                        },
                                                    });
                                                },
                                                visible: (order) =>
                                                    order.status != "payment_error" &&
                                                    order.parent_id == null,
                                            },
                                            {
                                                label: "Stampa Fattura",
                                                onClick: (order) => {
                                                    setConfirm({
                                                        title: "Sei sicuro?",
                                                        onConfirm: () => {
                                                            editOrder(order, {
                                                                invoice_printed: 0,
                                                                thermal_printer_id:
                                                                    config.thermal_printer_id || null,
                                                            });
                                                        },
                                                    });
                                                },
                                                visible: (order) =>
                                                    order.status != "payment_error" &&
                                                    apiSettings.mandant.invoices == 1 &&
                                                    order.bill == 4 &&
                                                    order.invoice == 1,
                                            },
                                            {
                                                label: "Riprova scontrino",
                                                onClick: (order) => {
                                                    setConfirm({
                                                        title: "Sei sicuro?",
                                                        onConfirm: () => {
                                                            updateOrder(order, {
                                                                bill: 1,
                                                                bill_number: null,
                                                                fiscal_printer_id:
                                                                    receiptPrinterId() || config.fiscal_printer_id || null,
                                                            });
                                                        },
                                                    });
                                                },
                                                visible: (order) => order.bill == 2 && !isPaxUncertain(order),
                                            },
                                            {
                                                // Esito PAX incerto (YELLGO-6292): il cliente e' stato mandato in cassa.
                                                // Se il PAX ha addebitato la carta l'ordine si conferma e lo scontrino esce
                                                // sulla stampante fiscale della cassa, senza un nuovo addebito: mai su un PAX,
                                                // che per lo scontrino con carta addebiterebbe di nuovo.
                                                label: "Conferma pagato",
                                                onClick: (order) => {
                                                    const printerId = receiptPrinterId();
                                                    if (!printerId && !config.virtual_printer_id) {
                                                        setConfirm({
                                                            title: "Errore",
                                                            message:
                                                                'Nessuna stampante fiscale senza PAX in questo negozio: lo scontrino emesso dal PAX addebiterebbe di nuovo la carta. Usa "Conferma pagato senza scontrino" se il PAX lo ha già stampato.',
                                                        });
                                                        return;
                                                    }
                                                    setConfirm({
                                                        title: "Il PAX ha addebitato la carta?",
                                                        message:
                                                            "Confermare solo se lo scontrino/estratto del PAX mostra l'addebito di questo ordine: escono comande e scontrino sulla stampante fiscale della cassa.",
                                                        onConfirm: () => {
                                                            updateOrder(order, {
                                                                status: "confirmed",
                                                                bill: 1,
                                                                bill_number: null,
                                                                fiscal_printer_id: printerId || null,
                                                                payment_error_code: null,
                                                                payment_error_message: null,
                                                            });
                                                        },
                                                    });
                                                },
                                                visible: (order) => isPaxUncertain(order),
                                            },
                                            {
                                                // Il PAX ha addebitato e lo scontrino e' uscito lo stesso (YELLGO-6258):
                                                // l'ordine si conferma e partono le comande, senza un secondo scontrino.
                                                label: "Conferma pagato senza scontrino",
                                                onClick: (order) => {
                                                    setConfirm({
                                                        title: "Lo scontrino è già uscito dal PAX?",
                                                        message:
                                                            "Confermare solo se il PAX ha addebitato la carta e ha già stampato lo scontrino di questo ordine: partono le comande, nessun altro scontrino.",
                                                        onConfirm: () => {
                                                            editOrder(order, {
                                                                status: "confirmed",
                                                                bill: 3,
                                                                bill_number: PAX_RECEIPT,
                                                                payment_error_code: null,
                                                                payment_error_message: null,
                                                            });
                                                        },
                                                    });
                                                },
                                                visible: (order) => isPaxUncertain(order),
                                            },
                                            {
                                                label: "Annulla ordine",
                                                onClick: (order) => {
                                                    setConfirm({
                                                        title: "Annullare l'ordine?",
                                                        message: "Solo se il PAX NON ha addebitato la carta: l'ordine viene archiviato.",
                                                        onConfirm: () => {
                                                            editOrder(order, {
                                                                status: "archived",
                                                                bill: 0,
                                                                bill_number: null,
                                                                payment_error_code: null,
                                                                payment_error_message: null,
                                                            });
                                                        },
                                                    });
                                                },
                                                visible: (order) => isPaxUncertain(order),
                                            },
                                            {
                                                label: "Preconto",
                                                onClick: (order) => {
                                                    setConfirm({
                                                        title: "Sei sicuro?",
                                                        onConfirm: () => {
                                                            updateOrder(order, {
                                                                prebill_request: 1,
                                                                prebill_printed: 0,
                                                            });
                                                        },
                                                    });
                                                },
                                                visible: (order) =>
                                                    order.status != "payment_error" &&
                                                    order.bill == 4 &&
                                                    order.invoice == 1,
                                            },
                                            {
                                                label: "Modifica Vendita",
                                                onClick: (order) => {
                                                    setConfirm({
                                                        title: "Sei sicuro?",
                                                        onConfirm: () => {
                                                            editOrder(order, { bill: 0 });
                                                            setSellsListVisible(false);
                                                        },
                                                    });
                                                },
                                                visible: (order) =>
                                                    order.bill == 1 &&
                                                    apiSettings.mandant.fiscal_printers == 1,
                                            },
                                            {
                                                label: "Annulla Vendita",
                                                onClick: (order) => {
                                                    const action = async () => {
                                                        const refundDojo = await hasDojoPayment(order);
                                                        setConfirm({
                                                            title: "Sei sicuro?",
                                                            message: refundDojo
                                                                ? "Il pagamento con carta (Dojo) verrà rimborsato al cliente."
                                                                : undefined,
                                                            onConfirm: () => {
                                                                if (!order.fiscal_printer_id && !order.virtual_printer_id) {
                                                                    cancelOrder(order, refundDojo);
                                                                    setSellsListVisible(false);
                                                                    return;
                                                                }
                                                                // Annullato lo scontrino si chiede se annullare anche l'ordine o rimetterlo tra gli aperti
                                                                setConfirm({
                                                                    title: "Annullare anche l'ordine?",
                                                                    message:
                                                                        "Si: l'ordine viene archiviato. No: l'ordine torna tra gli ordini aperti.",
                                                                    onConfirm: () => {
                                                                        cancelOrder(order, refundDojo, true);
                                                                        setSellsListVisible(false);
                                                                    },
                                                                    onCancel: () => {
                                                                        cancelOrder(order, refundDojo, false);
                                                                        setSellsListVisible(false);
                                                                    },
                                                                });
                                                            },
                                                        });
                                                    };
                                                    if (checkPermission("cancel_receipt", "view")) action();
                                                    else askPermission(action);
                                                },
                                                // lo scontrino stampato dal PAX non ha un numero Yellgo: si annulla sul PAX
                                                visible: (order) =>
                                                    order.bill == 3 &&
                                                    order.bill_number != PAX_RECEIPT &&
                                                    apiSettings.mandant.fiscal_printers == 1,
                                            },
                                        ],
                                    },
                                ]}
                                rows={filteredSells}
                            />
                        </div>
                        <div className="orders-list-pagination">
                            <button
                                type="button"
                                onClick={() => goToPage(currentPage - 1)}
                                disabled={currentPage <= 1}
                            >
                                Indietro
                            </button>
                            <span>
                                Pagina {currentPage} di {totalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() => goToPage(currentPage + 1)}
                                disabled={currentPage >= totalPages}
                            >
                                Avanti
                            </button>
                        </div>
                    </div>
                ),
            }}
        />
    );
}

export default SellsList;
